import { createFavoriteEntityId } from '../ids/entityIds.js';
import type { SceneNodeId } from '../scene/sceneState.js';
import type { AppState, SceneFavorite, SceneFavoriteTarget, ViewportTarget } from './appState.js';
import {
  getOwnerSceneFor,
  getViewportTargetLabel,
  viewportTargetFromSceneNode,
} from './viewportTargets.js';

export function sceneFavoriteTargetKey(target: SceneFavoriteTarget): string {
  switch (target.type) {
    case 'scene':
      return 'scene';
    case 'component':
      return `component:${target.componentId}`;
    case 'node':
      return `node:${target.componentId ?? 'scene'}:${target.nodeId}`;
  }
}

export function sceneFavoriteTargetForVirtual(target: 'scene' | 'component', componentId?: string | null): SceneFavoriteTarget | null {
  if (target === 'scene') return { type: 'scene' };
  if (!componentId) return null;
  return { type: 'component', componentId };
}

export function sceneFavoriteTargetForNode(componentId: string | null | undefined, nodeId: SceneNodeId): SceneFavoriteTarget {
  return componentId ? { type: 'node', componentId, nodeId } : { type: 'node', nodeId };
}

export function sceneFavoriteTargetToViewportTarget(appState: AppState, target: SceneFavoriteTarget): ViewportTarget | null {
  if (target.type === 'scene') return { type: 'scene' };
  if (target.type === 'component') return { type: 'component', componentId: target.componentId };
  return viewportTargetFromSceneNode(appState, target.componentId ?? null, target.nodeId);
}

export function isSceneFavoriteTargetValid(appState: AppState, target: SceneFavoriteTarget): boolean {
  if (target.type === 'scene') return true;
  if (target.type === 'component') return !!appState.componentLibrary[target.componentId];
  const scene = getOwnerSceneFor(appState, target.componentId);
  return !!scene?.nodes[target.nodeId];
}

export function getSceneFavoriteTargetLabel(appState: AppState, target: SceneFavoriteTarget): string {
  const viewportTarget = sceneFavoriteTargetToViewportTarget(appState, target);
  if (viewportTarget) return getViewportTargetLabel(appState, viewportTarget);
  if (target.type === 'component') return `Missing component: ${target.componentId}`;
  if (target.type === 'node') return `Missing node: ${target.nodeId}`;
  return 'Scene';
}

export function findSceneFavorite(appState: AppState, target: SceneFavoriteTarget): SceneFavorite | null {
  const key = sceneFavoriteTargetKey(target);
  return appState.sceneFavorites.find(favorite => sceneFavoriteTargetKey(favorite.target) === key) ?? null;
}

export function isSceneFavoriteTargetSaved(appState: AppState, target: SceneFavoriteTarget): boolean {
  return !!findSceneFavorite(appState, target);
}

export function addSceneFavorite(appState: AppState, target: SceneFavoriteTarget): SceneFavorite {
  const existing = findSceneFavorite(appState, target);
  if (existing) return existing;
  const favorite = { id: createFavoriteEntityId(), target };
  appState.sceneFavorites.push(favorite);
  return favorite;
}

export function removeSceneFavorite(appState: AppState, favoriteId: string): boolean {
  const index = appState.sceneFavorites.findIndex(favorite => favorite.id === favoriteId);
  if (index < 0) return false;
  appState.sceneFavorites.splice(index, 1);
  return true;
}

export function toggleSceneFavorite(appState: AppState, target: SceneFavoriteTarget): boolean {
  const existing = findSceneFavorite(appState, target);
  if (existing) {
    removeSceneFavorite(appState, existing.id);
    return false;
  }
  addSceneFavorite(appState, target);
  return true;
}

export function prepareViewportForSceneFavoriteNavigation(appState: AppState): boolean {
  if (appState.viewportMode !== 'isolation') return false;
  appState.viewportMode = 'focus';
  appState.viewportIsolationTarget = null;
  return true;
}
